import React, { useEffect, useRef, useState } from "react";
import { AzanSettings } from "./homeSettings";
import { AZAN_VOICES, azanVoice, limitAzan } from "./azanVoices";
import {
  OWN_VOICE,
  OwnAzanSlot,
  azanSource,
  checkRecording,
  ownAzanName,
  removeOwnAzan,
  saveOwnAzan,
} from "./ownAzan";

interface AzanOptionsProps {
  azan: AzanSettings;
  jamaatWord: string;
  onChange: (azan: AzanSettings) => void;
}

const MINUTES = [0, 5, 10, 15, 20, 30, 45, 60];

const AzanOptions: React.FC<AzanOptionsProps> = ({ azan, jamaatWord, onChange }) => {
  const [testing, setTesting] = useState<OwnAzanSlot | null>(null);
  const test = useRef<HTMLAudioElement | null>(null);
  const [names, setNames] = useState<Record<OwnAzanSlot, string | null>>({ main: null, fajr: null });
  const [problem, setProblem] = useState<{ slot: OwnAzanSlot; text: string } | null>(null);
  const pickers = { main: useRef<HTMLInputElement>(null), fajr: useRef<HTMLInputElement>(null) };
  const voiceId = azan.voice === OWN_VOICE && names.main ? OWN_VOICE : azanVoice(azan.voice).id;

  const stopTest = () => {
    test.current?.pause();
    test.current = null;
    setTesting(null);
  };

  const startTest = async (slot: OwnAzanSlot) => {
    stopTest();
    const sound = new Audio(await azanSource(azan.voice, slot === "fajr"));
    sound.volume = azan.volume;
    if (azan.length === "short") limitAzan(sound, azan.volume);
    sound.onended = stopTest;
    test.current = sound;
    sound.play().then(() => setTesting(slot)).catch(stopTest);
  };

  const pick = async (slot: OwnAzanSlot, file: File | undefined) => {
    if (!file) return;
    setProblem(null);
    const text = await checkRecording(file);
    if (text) {
      setProblem({ slot, text });
      return;
    }
    stopTest();
    await saveOwnAzan(slot, file);
    setNames((current) => ({ ...current, [slot]: file.name }));
    if (slot === "main") onChange({ ...azan, voice: OWN_VOICE });
  };

  const remove = async (slot: OwnAzanSlot) => {
    stopTest();
    await removeOwnAzan(slot);
    setNames((current) => ({ ...current, [slot]: null }));
    if (slot === "main" && azan.voice === OWN_VOICE) onChange({ ...azan, voice: AZAN_VOICES[0].id });
  };

  useEffect(() => {
    Promise.all([ownAzanName("main"), ownAzanName("fajr")]).then(([main, fajr]) =>
      setNames({ main, fajr }),
    );
  }, []);

  useEffect(() => {
    if (test.current) test.current.volume = azan.volume;
  }, [azan.volume]);

  useEffect(() => () => test.current?.pause(), []);

  const recording = (slot: OwnAzanSlot, label: string) => (
    <div className="hs-field">
      <span>{label}</span>
      <div className="hs-volume">
        {names[slot] ? (
          <>
            <span className="hs-file-name">{names[slot]}</span>
            {slot === "fajr" && (
              <button
                className="hs-button"
                onClick={() => (testing === "fajr" ? stopTest() : startTest("fajr"))}
              >
                {testing === "fajr" ? "Stop" : "Test"}
              </button>
            )}
            <button className="hs-button" onClick={() => remove(slot)}>
              Remove
            </button>
          </>
        ) : (
          <button className="hs-button" onClick={() => pickers[slot].current?.click()}>
            Choose a recording…
          </button>
        )}
        <input
          ref={pickers[slot]}
          type="file"
          accept="audio/*"
          hidden
          onChange={(e) => {
            pick(slot, e.target.files?.[0]);
            e.target.value = "";
          }}
        />
      </div>
      {problem?.slot === slot && (
        <p className="hs-problem" role="alert">
          {problem.text}
        </p>
      )}
    </div>
  );

  return (
    <div className="hs-wording">
      <label className="hs-toggle">
        <input
          type="checkbox"
          checked={azan.atStart}
          onChange={(e) => onChange({ ...azan, atStart: e.target.checked })}
        />
        <span>Azan at the start time of each prayer</span>
      </label>
      <label className="hs-toggle">
        <input
          type="checkbox"
          checked={azan.beforeJamaah}
          onChange={(e) => onChange({ ...azan, beforeJamaah: e.target.checked })}
        />
        <span>Azan before the {jamaatWord}</span>
        <select
          className="hs-select"
          value={azan.minutesBefore}
          disabled={!azan.beforeJamaah}
          onChange={(e) => onChange({ ...azan, minutesBefore: Number(e.target.value) })}
          aria-label="Minutes before"
        >
          {MINUTES.map((m) => (
            <option key={m} value={m}>
              {m === 0 ? "at the time" : `${m} min before`}
            </option>
          ))}
        </select>
      </label>
      <label className="hs-field">
        <span>Voice</span>
        <select
          className="hs-select"
          value={voiceId}
          onChange={(e) => onChange({ ...azan, voice: e.target.value })}
        >
          {AZAN_VOICES.map((v) => (
            <option key={v.id} value={v.id}>
              {v.name}
            </option>
          ))}
          {names.main && <option value={OWN_VOICE}>Your own recording</option>}
        </select>
      </label>
      {recording("main", "Your own recording")}
      {recording("fajr", "Fajr recording (optional)")}
      <p className="hs-note">
        Choose an audio file on this tablet. Recordings stay on this tablet; they aren't uploaded. A
        Fajr recording plays at Fajr whichever voice is chosen.
      </p>
      <label className="hs-field">
        <span>Play</span>
        <select
          className="hs-select"
          value={azan.length ?? "full"}
          onChange={(e) => onChange({ ...azan, length: e.target.value as AzanSettings["length"] })}
        >
          <option value="full">The full azan</option>
          <option value="short">The first 30 seconds</option>
        </select>
      </label>
      <label className="hs-field">
        <span>Volume</span>
        <span className="hs-volume">
          <input
            type="range"
            min={0.1}
            max={1}
            step={0.1}
            value={azan.volume}
            onChange={(e) => onChange({ ...azan, volume: Number(e.target.value) })}
            aria-label="Azan volume"
          />
          <button
            className="hs-button"
            onClick={() => (testing === "main" ? stopTest() : startTest("main"))}
          >
            {testing === "main" ? "Stop" : "Test"}
          </button>
        </span>
      </label>
      <p className="hs-note">
        On Fridays it plays for the first Jumu'ah only. It never plays over a live broadcast in the
        bar; an open player or video closes for it.
      </p>
    </div>
  );
};

export default AzanOptions;
